"use client";

import { normalizeNumberGrouping } from "@/lib/locale-formatting";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import { MapPin, ExternalLink, List, LayoutGrid, Pencil } from "lucide-react";
import { ReportButton } from "../report/report-button";
import { Event, Venue, CityConfig, FestivalSummary } from "@/lib/types";
import { DateRangeValue } from "@/lib/constants";
import {
  getUniqueGenres,
  getOtherSubGenres,
  filterByGenres,
  filterByDateRange,
  filterByTimeOfDay,
  TimeOfDay,
} from "@/lib/utils";
import { FilterBar } from "./filter-bar";
import { EventsGrid } from "./events-grid";
import { ListSidebar } from "./list-sidebar";
import { useSetSpotifyCitySlug } from "../spotify/use-set-city-slug";
import { useSpotifyPlayer } from "../spotify/spotify-player-context";
import { useJukeboxReopen } from "../spotify/use-jukebox-reopen";
import { useJukeboxLauncher } from "../spotify/use-jukebox-launcher";
import type { JukeboxLaunchTarget } from "../spotify/spotify-player-context";
import { dedupeCityJukeboxPlaylist, launchJukeboxAt, resumeIndexAfterLaunch, toJukeboxEvents } from "@/lib/jukebox-playlist";
import { AuthProvider, useAuth } from "../auth/auth-context";
import { SubscribeButton } from "../subscribe-button";
import { UserMenu } from "../auth/user-menu";
import { LanguageSwitcher } from "../language-switcher";
import { SearchButton } from "../search/search-button";
import { VenueMapPopup } from "../map/venue-map-popup";
import { User } from "@supabase/supabase-js";
import { GenreJukeboxCta } from "./genre-jukebox-cta";
import { GenreJukeboxModal } from "./genre-jukebox-modal";
import { VenueEditModal } from "../admin/venue-edit-modal";

function AdminVenueEditButton({ venue, citySlug }: { venue: Venue; citySlug: string }) {
  const { isAdmin } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  if (!isAdmin) return null;
  return (
    <>
      <button
        onClick={() => setEditOpen(true)}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-neutral-700 text-neutral-500 transition-colors hover:border-acid hover:text-acid"
        title="Edit venue"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      {editOpen && (
        <VenueEditModal
          venue={{
            venue_id: venue.id,
            name: venue.name,
            slug: venue.slug,
            city_slug: citySlug,
            address: venue.address,
            url: venue.url,
          }}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
}

interface VenuePageClientProps {
  events: Event[];
  festivals?: FestivalSummary[];
  city: CityConfig;
  venue: Venue;
  blockedSpotifyIds: string[];
  initialUser?: User | null;
}

export function VenuePageClient({
  events,
  festivals,
  city,
  venue,
  blockedSpotifyIds,
  initialUser = null,
}: VenuePageClientProps) {
  const t = useTranslations("Events");
  const tCommon = useTranslations("Common");
  const tJukebox = useTranslations("Jukebox");
  const [view, setView] = useState<"list" | "grid">("list");
  const [showMap, setShowMap] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [genreMatchMode, setGenreMatchMode] = useState<"or" | "and">("or");
  const [dateRange, setDateRange] = useState<DateRangeValue>("all");
  const [onlyWithAudio, setOnlyWithAudio] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string | null>(null);
  const [customEndDate, setCustomEndDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<TimeOfDay | null>(null);
  const [jukeboxOpen, setJukeboxOpen] = useState(false);

  useSetSpotifyCitySlug(city.slug);
  const { artistId: spotifyActive } = useSpotifyPlayer();

  const genres = useMemo(() => getUniqueGenres(events), [events]);
  const otherSubGenres = useMemo(() => getOtherSubGenres(events), [events]);

  const dateFilteredEvents = useMemo(
    () => filterByDateRange(events, dateRange, customStartDate, customEndDate, city.timezone),
    [events, dateRange, customStartDate, customEndDate, city.timezone]
  );

  const filteredEvents = useMemo(() => {
    let result = dateFilteredEvents;
    result = filterByGenres(result, selectedGenres, genreMatchMode);
    result = filterByTimeOfDay(result, selectedTime);
    if (onlyWithAudio) {
      result = result.filter((e) =>
        e.attractions.some((a) => a.spotify_id && !blockedSpotifyIds.includes(a.spotify_id))
      );
    }
    return result;
  }, [dateFilteredEvents, selectedGenres, genreMatchMode, selectedTime, onlyWithAudio, blockedSpotifyIds]);

  // Jukebox: the venue's date-filtered bills with Spotify artists, kept
  // before series deduplication too so a card can open on a dropped bill.
  const jukeboxSource = useMemo(
    () => toJukeboxEvents(dateFilteredEvents, blockedSpotifyIds),
    [dateFilteredEvents, blockedSpotifyIds]
  );
  const jukeboxPlaylist = useMemo(() => dedupeCityJukeboxPlaylist(jukeboxSource), [jukeboxSource]);

  // Set when a card's play button opened the jukebox on its own bill.
  const [jukeboxLaunch, setJukeboxLaunch] = useState<JukeboxLaunchTarget | null>(null);
  const jukeboxStart = useMemo(
    () => jukeboxLaunch
      ? launchJukeboxAt(jukeboxPlaylist, jukeboxSource, jukeboxLaunch.event, jukeboxLaunch.artistId, blockedSpotifyIds)
      : null,
    [jukeboxLaunch, jukeboxPlaylist, jukeboxSource, blockedSpotifyIds]
  );

  const jukeboxArtistCount = useMemo(
    () => jukeboxPlaylist.reduce((sum, je) => sum + je.artists.length, 0),
    [jukeboxPlaylist]
  );

  const sessionKey = `jukebox-venue-${city.slug}-${venue.slug}`;

  // Resume jukebox where user left off if the selected dates haven't changed.
  const jukeboxSessionRef = useRef<{
    index: number;
    dateRange: DateRangeValue;
    customStartDate: string | null;
    customEndDate: string | null;
  } | null>(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(sessionKey);
      if (raw) jukeboxSessionRef.current = JSON.parse(raw);
    } catch {}
  }, [sessionKey]);

  const getJukeboxResumeIndex = useCallback((): number => {
    const saved = jukeboxSessionRef.current;
    if (!saved || saved.dateRange !== dateRange) return 0;
    if (dateRange === "custom" && (
      saved.customStartDate !== customStartDate || saved.customEndDate !== customEndDate
    )) return 0;
    return saved.index;
  }, [dateRange, customStartDate, customEndDate]);

  const handleJukeboxClose = useCallback((lastIndex: number) => {
    const session = { index: resumeIndexAfterLaunch(jukeboxStart, jukeboxPlaylist, lastIndex), dateRange, customStartDate, customEndDate };
    jukeboxSessionRef.current = session;
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify(session));
    } catch {}
    setJukeboxOpen(false);
    setJukeboxLaunch(null);
  }, [sessionKey, dateRange, customStartDate, customEndDate, jukeboxStart, jukeboxPlaylist]);

  useJukeboxReopen(jukeboxOpen, () => {
    const saved = jukeboxSessionRef.current;
    if (saved) {
      setDateRange(saved.dateRange);
      setCustomStartDate(saved.customStartDate);
      setCustomEndDate(saved.customEndDate);
    }
    setJukeboxOpen(true);
  });

  const launchJukeboxFromCard = useCallback((target: JukeboxLaunchTarget) => {
    setJukeboxLaunch(target);
    setJukeboxOpen(true);
  }, []);
  useJukeboxLauncher(launchJukeboxFromCard);

  return (
    <AuthProvider initialUser={initialUser}>
        <div className="min-h-screen bg-dark font-sans text-white">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                {/* Breadcrumb */}
                <div className="mb-3 flex items-center gap-2">
                  <Link
                    href="/"
                    className="flex items-center gap-2 transition-opacity hover:opacity-80"
                  >
                    <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-acid shadow-[0_0_10px_#D1F805]" />
                    <span className="font-mono text-xs tracking-[0.2em] text-neutral-500">
                      {tCommon("brand")}
                    </span>
                  </Link>
                  <span className="font-mono text-xs text-neutral-700">/</span>
                  <Link
                    href={`/${city.slug}`}
                    className="font-mono text-xs text-neutral-500 transition-colors hover:text-acid"
                  >
                    {city.name.toUpperCase()}
                  </Link>
                </div>

                {/* Venue info */}
                <div className="flex items-start gap-4">
                  {venue.image_url && (
                    <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-neutral-800">
                      <Image
                        src={venue.image_url}
                        alt={venue.name}
                        fill
                        className="object-cover"
                        sizes="64px"
                      />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-3">
                      <h1 className="font-mono text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
                        {venue.name}
                      </h1>
                      <SubscribeButton
                        type="venue"
                        targetId={venue.id}
                        targetName={venue.name}
                        targetImage={venue.image_url}
                        size="md"
                      />
                    </div>
                    {venue.address && (
                      <button
                        onClick={() => setShowMap(true)}
                        className="mt-1 flex cursor-pointer items-center gap-1.5 group"
                      >
                        <MapPin className="h-3 w-3 shrink-0 text-neutral-500 transition-colors group-hover:text-acid" />
                        <span className="font-mono text-xs text-neutral-400 transition-colors group-hover:text-acid underline decoration-neutral-700 underline-offset-2 group-hover:decoration-acid/50">
                          {venue.address}
                        </span>
                      </button>
                    )}
                    <div className="mt-1 flex items-center gap-3">
                      <p className="font-mono text-sm text-neutral-500">
                        {normalizeNumberGrouping(t("showCount", { count: filteredEvents.length }))}
                      </p>
                      {venue.url && (
                        <a
                          href={venue.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 font-mono text-xs text-neutral-500 transition-colors hover:text-acid"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {tCommon("website")}
                        </a>
                      )}
                      <ReportButton
                        entityType="venue"
                        entityId={venue.id}
                        entityName={venue.name}
                      />
                      <AdminVenueEditButton venue={venue} citySlug={city.slug} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-3">
                {/* View toggle */}
                <div className="flex w-fit gap-1 rounded-lg border border-neutral-800 bg-surface p-1">
                  <button
                    onClick={() => setView("list")}
                    className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 font-mono text-xs transition-colors ${
                      view === "list"
                        ? "bg-acid text-black"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    <List className="h-3.5 w-3.5" />
                    {t("list")}
                  </button>
                  <button
                    onClick={() => setView("grid")}
                    className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 font-mono text-xs transition-colors ${
                      view === "grid"
                        ? "bg-acid text-black"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    {t("grid")}
                  </button>
                </div>
                <SearchButton />
                <LanguageSwitcher />
                <UserMenu />
              </div>
            </div>

            {/* Filters */}
            <FilterBar
              genres={genres}
              otherSubGenres={otherSubGenres}
              selectedGenres={selectedGenres}
              onGenresChange={setSelectedGenres}
              genreMatchMode={genreMatchMode}
              onMatchModeChange={setGenreMatchMode}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomStartDateChange={setCustomStartDate}
              onCustomEndDateChange={setCustomEndDate}
              selectedTime={selectedTime}
              onTimeChange={setSelectedTime}
              onlyWithAudio={onlyWithAudio}
              onOnlyWithAudioChange={setOnlyWithAudio}
            />

            {jukeboxPlaylist.length > 0 && (
              <div className="mt-6">
                <GenreJukeboxCta
                  headline={tJukebox("ctaHeadlineVenue", { venue: venue.name })}
                  artistCount={jukeboxArtistCount}
                  cityName={venue.name}
                  onClick={() => setJukeboxOpen(true)}
                />
              </div>
            )}
          </div>

          {/* Events */}
          <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${spotifyActive ? "pb-[220px]" : "pb-12"}`}>
            {view === "list" ? (
              <div className="lg:flex lg:items-start lg:gap-8">
                <div className="min-w-0 flex-1">
                  <EventsGrid
                    events={filteredEvents}
                    festivals={festivals}
                    blockedSpotifyIds={blockedSpotifyIds}
                    citySlug={city.slug}
                    view={view}
                    fillWidth
                  />
                </div>
                <ListSidebar cityName={city.name} events={events} citySlug={city.slug} />
              </div>
            ) : (
              <EventsGrid
                events={filteredEvents}
                festivals={festivals}
                blockedSpotifyIds={blockedSpotifyIds}
                citySlug={city.slug}
                view={view}
              />
            )}
          </div>
        </div>

        {/* Map popup */}
        {showMap && venue.address && (
          <VenueMapPopup
            venueName={venue.name}
            address={venue.address}
            latitude={venue.latitude}
            longitude={venue.longitude}
            onClose={() => setShowMap(false)}
          />
        )}

        {/* Jukebox modal */}
        {jukeboxOpen && (jukeboxStart || jukeboxPlaylist.length > 0) && (
          <GenreJukeboxModal
            playlist={jukeboxStart ? jukeboxStart.playlist : jukeboxPlaylist}
            genreName={venue.name}
            selectedGenres={[]}
            citySlug={city.slug}
            titleOverride={tJukebox("venueTitle")}
            // eslint-disable-next-line react-hooks/refs
            initialIndex={jukeboxStart ? jukeboxStart.index : getJukeboxResumeIndex()}
            initialArtistId={jukeboxStart?.artistId}
            onClose={handleJukeboxClose}
          />
        )}
    </AuthProvider>
  );
}
